import { inject, Injectable } from '@angular/core';
import { AddLayerObject, LayerSpecification, RasterLayerSpecification, RasterSourceSpecification, SourceSpecification } from 'maplibre-gl';
import { IRasterLayer } from '../../../interfaces/sjmap2/raster/IRaster';
import { TitilerService } from '../../titiler.service';
import { v4 as uuidv4 } from 'uuid';
import { IVectorLayer } from '../../../interfaces/sjmap2/vector/IVector';
import { IVectorSource } from '../../../interfaces/sjmap2/vector/IVectorSource';
import { IRasterSource } from '../../../interfaces/sjmap2/raster/IRasterSource';
import { TColormap } from '../../../types/explorer/sjmap2/colormap/TColormap';
import { MartinService } from '../../shared/martin.service';

/* WARNING: Queste interfacce servono solo per questo file */
interface IMapLibreSourceInfo {
  name: string,
  sourceSpec: SourceSpecification
}
interface IBaseMapLibreInfo {
  source: IMapLibreSourceInfo
}
interface IRasterMapLibreInfo extends IBaseMapLibreInfo {
  layer: RasterLayerSpecification
}
interface IVectorMapLibreInfo extends IBaseMapLibreInfo {
  layers: AddLayerObject[]
}
export interface IBoundsPolygon {
  type: "Polygon";
  coordinates: number[][][];
}
/*---------------------------------------------------------*/

@Injectable({
  providedIn: 'root'
})
export class ExplorerSjmapToMaplibreService {

  private titilerService: TitilerService = inject(TitilerService);
  private martinService: MartinService = inject(MartinService);

  constructor() { }

  fromRasterLayerToMaplibre(rasterLayer: IRasterLayer): IRasterMapLibreInfo[] {
    let _source: IMapLibreSourceInfo;
    let _layer: RasterLayerSpecification;

    let _maplibreInfos: IRasterMapLibreInfo[] = [];

    rasterLayer.sources.forEach((layerSource) => {
      _source = this.fromRasterLayerToMaplibreSource(layerSource, layerSource.colormap, rasterLayer.bounds);
      _layer = this.fromRasterLayerToMaplibreLayer(_source);

      _maplibreInfos.push({
        source: _source,
        layer: _layer
      });
    });

    return _maplibreInfos;  
  }

  private fromRasterLayerToMaplibreSource(rasterSource: IRasterSource, colormap: TColormap, layerBounds: IBoundsPolygon): IMapLibreSourceInfo {
    let _sourceSpec: RasterSourceSpecification;
    const coords = layerBounds.coordinates[0];
    const bbox = coords.reduce((bounds: number[], coord: number[]) => {
      return [
          Math.min(coord[0], bounds[0]), // minLon (west)
          Math.min(coord[1], bounds[1]), // minLat (south)
          Math.max(coord[0], bounds[2]), // maxLon (east)
          Math.max(coord[1], bounds[3])  // maxLat (north)
      ];
  }, [Infinity, Infinity, -Infinity, -Infinity]) as [number, number, number, number];
    if (rasterSource.source.startsWith('s3') || rasterSource.source.startsWith('http')) {
      _sourceSpec = {
        type: "raster",
        bounds: bbox,
        tiles: [
          this.titilerService.getTilesURL(rasterSource.source, colormap, {bidx: 1})
        ],
      }
    }
    return {
      name: uuidv4().toString() + "-source",
      sourceSpec: _sourceSpec
    };
  }

  private fromRasterLayerToMaplibreLayer(addSourceObject: IMapLibreSourceInfo): RasterLayerSpecification {
    return {
      id: uuidv4().toString() + "-raster-tile-layer",
      source: addSourceObject.name,
      type: "raster",
      layout: {
        visibility: "none"
      }
    };
  }

  fromVectorLayerToMaplibre(vectorLayer: IVectorLayer): IVectorMapLibreInfo[] {
    let _source: IMapLibreSourceInfo;
    let _layers: AddLayerObject[];

    let _maplibreInfos: IVectorMapLibreInfo[] = [];

    vectorLayer.sources.forEach((vectorSource) => {
      _source = this.fromVectorLayerToMaplibreSource(vectorSource);
      _layers = this.fromVectorLayerToMaplibreLayer(_source, vectorSource);

      _maplibreInfos.push({
        source: _source,
        layers: _layers
      });
    });

    return _maplibreInfos;
  }

  private fromVectorLayerToMaplibreSource(vectorSource: IVectorSource): IMapLibreSourceInfo {
    let _sourceSpec: SourceSpecification;

    if (this.martinService.isMartinSource(vectorSource)) {
      _sourceSpec = {
        type: "vector",
        url: this.martinService.buildTileJsonUrl(vectorSource.source)
      };
    } else if ((vectorSource.source as string).startsWith('http')) {
      // PMTiles path: unchanged for type === "pmtiles".
      _sourceSpec = {
        type: "vector",
        url: "pmtiles://" + vectorSource.source
      };
    }

    return {
      name: uuidv4().toString(),
      sourceSpec: _sourceSpec
    };
  }

  private getRandomColor(): string {
    return `#${Math.floor(Math.random()*16777215).toString(16)}`;
  }

  private fromVectorLayerToMaplibreLayer(addSourceObject: IMapLibreSourceInfo, vectorSource: IVectorSource): LayerSpecification[] {
    const layers: LayerSpecification[] = [];
    const baseId = uuidv4().toString();

    layers.push({
      id: `${baseId}-point`,
      source: addSourceObject.name,
      'source-layer': vectorSource.source_layer,
      type: vectorSource.style_obj["type"],
      paint: vectorSource.style_obj["paint"],
    } as LayerSpecification);

    return layers;
  }

}
